import React, { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router'
import AuthLayout from './components/AuthLayout'
import AuthService from 'services/auth'
import hashPassword from 'utils/hash/hashPassword'
import { Box, useToast } from '@chakra-ui/react'
import AuthAlert from './components/AuthAlert'
import { USER_ROLES } from 'constants/users'
import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_USER_INFO, LOGIN_USER_V2 } from 'services/auth/graphql'
import { API_V2 } from 'constants/apollo'

const AuthScreen = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const [loginUser] = useMutation(LOGIN_USER_V2, {
    context: {
      clientName: API_V2,
    },
  })

  const [getUserInfo] = useLazyQuery(GET_USER_INFO, {
    fetchPolicy: 'network-only',
  })

  const onEmailChange = useCallback(e => setEmail(e.target.value), [setEmail])

  const onPasswordChange = useCallback(
    e => setPassword(e.target.value),
    [setPassword],
  )
  const onLoginFormSubmit = useCallback(
    async e => {
      e.preventDefault()
      setIsLoading(true)

      const hash = await hashPassword(password)

      try {
        const { data } = await loginUser({
          variables: {
            credentials: {
              email,
              password: hash,
            },
          },
        })

        AuthService.setCredentials({ role: false })

        if (data?.login?.success) {
          const { data: userData } = await getUserInfo()

          if (
            userData?.getUserInfo?.role &&
            Object.hasOwnProperty.call(USER_ROLES, userData?.getUserInfo?.role)
          ) {
            AuthService.setCredentials({ role: userData.getUserInfo.role })
            navigate('/')
          } else {
            toast({
              position: 'top',
              render: ({ onClose }) => (
                <Box mt='61px'>
                  <AuthAlert
                    onClose={onClose}
                    title='Permission denied!'
                    reason='You haven`t access to this panel'
                  />
                </Box>
              ),
              duration: 5000,
            })

            localStorage.removeItem('role')
          }
        }
      } catch (err) {
        toast({
          position: 'top',
          render: ({ onClose }) => (
            <Box mt='61px'>
              <AuthAlert
                onClose={onClose}
                title='Wrong credentials'
                reason='Check your credentials or try later'
              />
            </Box>
          ),
          duration: 5000,
        })
        localStorage.removeItem('role')
      }

      setIsLoading(false)
    },
    [email, getUserInfo, loginUser, navigate, password, toast],
  )

  useEffect(() => {
    const loginButton = document.querySelector('.login-button')
    const passwordField = document.getElementById('password')

    const checkEnterPress = e => {
      if (e.code === 'Enter' || e.code === 'NumpadEnter') {
        if (loginButton) {
          passwordField?.blur()
          loginButton.click()
        }
      }
    }

    if (passwordField) {
      passwordField.addEventListener('keypress', checkEnterPress)
    }

    return () => {
      passwordField?.removeEventListener('keypress', checkEnterPress)
    }
  })

  return !AuthService.token ? (
    <AuthLayout
      email={email}
      onEmailChange={onEmailChange}
      onPasswordChange={onPasswordChange}
      password={password}
      onLoginFormSubmit={onLoginFormSubmit}
      isLoading={isLoading}
    />
  ) : (
    <Navigate to='/' />
  )
}
export default AuthScreen
